// src/modules/Parameters/PreparationSetup.js
import React, { useState, useMemo, useEffect, useCallback } from 'react';
import { auth } from '../../firebaseConfig';
import useFirestore from '../../hooks/useFirestore';
import usePagePermissions from '../../hooks/usePagePermissions';

// Import global components
import {
    PageHeader,
    SearchBar,
    FormModal,
    ConfirmModal,
    TableSkeleton,
    EmptyState,
    Pagination,
} from '../../components/common';

// Import utilities
import { getTimestamps } from '../../utils/timestamps';

// Page-specific styles
import styles from './Parameters.module.css';

const PREPARATION_SETUP_COLLECTION = 'parameters/production/setups-preparo';
const ITEMS_PER_PAGE = 10;

const PreparationSetupPage = () => {
    const can = usePagePermissions('parametros_de_preparo');

    // --- Estados Principais ---
    const [editingItem, setEditingItem] = useState(null);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [formStatus, setFormStatus] = useState({ message: '', type: '' });
    const [formLoading, setFormLoading] = useState(false);

    // --- Estados para Busca e Paginação ---
    const [searchTerm, setSearchTerm] = useState('');
    const [currentPage, setCurrentPage] = useState(1);

    // --- Estados do Formulário ---
    const [setupName, setSetupName] = useState('');
    const [setupDescription, setSetupDescription] = useState('');
    const [preparationSteps, setPreparationSteps] = useState([]);
    const [currentStep, setCurrentStep] = useState('');
    const [situation, setSituation] = useState('Ativo');

    // --- Estado para verificação de duplicados ---
    const [nameExistsError, setNameExistsError] = useState('');

    // --- Estado para Delete ---
    const [deleteConfirm, setDeleteConfirm] = useState({ isOpen: false, id: null, name: '' });
    const [deleteLoading, setDeleteLoading] = useState(false);

    // --- Hook Firestore ---
    const {
        data: setups,
        loading: listLoading,
        // error: listError,  // <-- REMOVED: Use error handling via try/catch instead
        addDocument: createDocument,
        updateDocument,
        deleteDocument,
        refreshData
    } = useFirestore(PREPARATION_SETUP_COLLECTION);

    const SITUATION_OPTIONS = ['Ativo', 'Inativo'];

    // ============================================================
    // 1. DEFINIR filteredSetups ANTES dos callbacks que o usam
    // ============================================================
    const filteredSetups = useMemo(() => {
        if (!setups) return [];
        const lowerSearch = searchTerm.toLowerCase();

        return setups.filter(item => {
            const matchesSearch = (
                (item.name && item.name.toLowerCase().includes(lowerSearch)) ||
                (item.description && item.description.toLowerCase().includes(lowerSearch))
            );
            return matchesSearch;
        }).sort((a, b) => (a.name || '').localeCompare(b.name || ''));
    }, [setups, searchTerm]);

    const totalFilteredItems = filteredSetups.length;
    const totalPagesAfterFilter = Math.ceil(totalFilteredItems / ITEMS_PER_PAGE);

    const currentSetups = useMemo(() => {
        const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
        return filteredSetups.slice(startIndex, startIndex + ITEMS_PER_PAGE);
    }, [filteredSetups, currentPage]);

    // --- Verificação de nome duplicado ---
    const checkDuplicateName = useCallback((name, excludeId = null) => {
        if (!setups) return false;
        return setups.some(item =>
            item.name?.toLowerCase() === name.toLowerCase() &&
            item.id !== excludeId
        );
    }, [setups]);

    // --- Validação em tempo real do nome ---
    useEffect(() => {
        if (setupName.trim()) {
            const isDuplicate = checkDuplicateName(setupName, editingItem?.id);
            setNameExistsError(isDuplicate ? 'Já existe um setup com este nome.' : '');
        } else {
            setNameExistsError('');
        }
    }, [setupName, checkDuplicateName, editingItem]);

    // --- Handlers para Modo de Preparo ---
    const handleAddPreparationStep = useCallback(() => {
        const trimmedStep = currentStep.trim();
        if (!trimmedStep) return;

        const newStep = {
            id: Date.now().toString() + Math.random().toString(36).substr(2, 9),
            order: preparationSteps.length + 1,
            description: trimmedStep,
            createdAt: new Date().toISOString()
        };

        setPreparationSteps(prev => [...prev, newStep]);
        setCurrentStep('');
    }, [currentStep, preparationSteps.length]);

    const handleUpdatePreparationStep = useCallback((id, value) => {
        setPreparationSteps(prev => prev.map(step =>
            step.id === id ? { ...step, description: value, updatedAt: new Date().toISOString() } : step
        ));
    }, []);

    const handleRemovePreparationStep = useCallback((idToRemove) => {
        const updatedSteps = preparationSteps
            .filter(step => step.id !== idToRemove)
            .map((step, index) => ({ ...step, order: index + 1 }));
        setPreparationSteps(updatedSteps);
    }, [preparationSteps]);

    const handleMovePreparationStep = useCallback((index, direction) => {
        const list = [...preparationSteps];
        if ((direction === 'up' && index === 0) || (direction === 'down' && index === list.length - 1)) return;
        const newIndex = direction === 'up' ? index - 1 : index + 1;
        [list[index], list[newIndex]] = [list[newIndex], list[index]];

        const updatedList = list.map((step, i) => ({ ...step, order: i + 1 }));
        setPreparationSteps(updatedList);
    }, [preparationSteps]);

    const handleStepKeyPress = useCallback((e) => {
        if (e.key === 'Enter') {
            e.preventDefault();
            handleAddPreparationStep();
        }
    }, [handleAddPreparationStep]);

    // --- Reset do Formulário ---
    const resetForm = useCallback(() => {
        setEditingItem(null);
        setIsModalOpen(false);
        setSetupName('');
        setSetupDescription('');
        setPreparationSteps([]);
        setCurrentStep('');
        setSituation('Ativo');
        setFormStatus({ message: '', type: '' });
        setNameExistsError('');
    }, []);

    const handleCloseModal = useCallback(() => {
        resetForm();
    }, [resetForm]);

    const handleNewClick = useCallback(() => {
        resetForm();
        setIsModalOpen(true);
    }, [resetForm]);

    const handleEditClick = useCallback((item) => {
        setEditingItem(item);
        setSetupName(item.name || '');
        setSetupDescription(item.description || '');
        setPreparationSteps(item.preparationSteps || []);
        setSituation(item.situation || 'Ativo');
        setFormStatus({ message: '', type: '' });
        setNameExistsError('');
        setIsModalOpen(true);
    }, []);

    const handleDeleteClick = useCallback((id, name) => {
        setDeleteConfirm({ isOpen: true, id, name });
    }, []);

    // Confirmar Delete
    const handleConfirmDelete = useCallback(async () => {
        const { id, name } = deleteConfirm;
        setDeleteLoading(true);

        try {
            const result = await deleteDocument(id);

            if (result.success) {
                setFormStatus({ message: `Setup "${name}" deletado com sucesso!`, type: 'success' });
                setDeleteConfirm({ isOpen: false, id: null, name: '' });

                const remainingItems = filteredSetups.length - 1;
                if (currentPage > 1 && remainingItems <= (currentPage - 1) * ITEMS_PER_PAGE) {
                    setCurrentPage(prev => prev - 1);
                }

                setTimeout(() => setFormStatus({ message: '', type: '' }), 3000);
            } else {
                setFormStatus({ message: `Erro ao deletar: ${result.error}`, type: 'error' });
                setDeleteConfirm({ isOpen: false, id: null, name: '' });
                setTimeout(() => setFormStatus({ message: '', type: '' }), 5000);
            }
        } catch (error) {
            setFormStatus({ message: 'Erro ao deletar setup. Tente novamente.', type: 'error' });
            setDeleteConfirm({ isOpen: false, id: null, name: '' });
        } finally {
            setDeleteLoading(false);
        }
    }, [deleteConfirm, deleteDocument, filteredSetups, currentPage]);

    // --- Salvar Setup ---
    const handleSave = useCallback(async (e) => {
        e.preventDefault();
        setFormStatus({ message: '', type: '' });

        if (!setupName || preparationSteps.length === 0) {
            setFormStatus({
                message: 'Nome do setup e pelo menos uma etapa de preparo são obrigatórios.',
                type: 'error'
            });
            return;
        }

        if (checkDuplicateName(setupName, editingItem?.id)) {
            setFormStatus({
                message: 'Já existe um setup com este nome. Por favor, escolha um nome diferente.',
                type: 'error'
            });
            return;
        }

        setFormLoading(true);

        try {
            const currentUser = auth.currentUser;
            const userInfo = currentUser ? {
                updatedBy: currentUser.email || currentUser.uid,
                updatedByName: currentUser.displayName || currentUser.email?.split('@')[0] || 'Usuário'
            } : {
                updatedBy: 'sistema',
                updatedByName: 'Sistema'
            };

            const setupToSave = {
                name: setupName.trim(),
                description: setupDescription.trim(),
                preparationSteps: preparationSteps.map(step => ({
                    id: step.id,
                    order: step.order,
                    description: step.description,
                    createdAt: step.createdAt || new Date().toISOString()
                })),
                situation: situation,
                ...userInfo,
                updatedAt: new Date().toISOString(),
            };

            let result;
            if (editingItem) {
                const updateData = {
                    ...setupToSave,
                    previousVersion: {
                        name: editingItem.name,
                        description: editingItem.description,
                        preparationSteps: editingItem.preparationSteps,
                        updatedAt: editingItem.updatedAt,
                        updatedBy: editingItem.updatedBy
                    }
                };
                result = await updateDocument(editingItem.id, updateData);
                if (result.success) {
                    setFormStatus({ message: 'Setup atualizado com sucesso!', type: 'success' });
                    setTimeout(() => {
                        resetForm();
                    }, 1500);
                } else {
                    setFormStatus({ message: `Falha ao atualizar: ${result.error}`, type: 'error' });
                }
            } else {
                result = await createDocument({
                    ...setupToSave,
                    createdAt: new Date().toISOString(),
                    createdBy: userInfo.updatedBy,
                    createdByName: userInfo.updatedByName
                });
                if (result.success) {
                    setFormStatus({ message: 'Setup criado com sucesso!', type: 'success' });
                    setTimeout(() => {
                        resetForm();
                    }, 1500);
                } else {
                    setFormStatus({ message: `Falha ao criar: ${result.error}`, type: 'error' });
                }
            }
        } catch (error) {
            console.error('Erro ao salvar setup:', error);
            setFormStatus({ message: `Erro: ${error.message}`, type: 'error' });
        } finally {
            setFormLoading(false);
        }
    }, [setupName, setupDescription, preparationSteps, situation, editingItem, checkDuplicateName, createDocument, updateDocument, resetForm]);

    // --- Paginação ---
    const handlePageChange = useCallback((newPage) => {
        if (newPage >= 1 && newPage <= totalPagesAfterFilter) {
            setCurrentPage(newPage);
        }
    }, [totalPagesAfterFilter]);

    const handleSearch = useCallback((term) => {
        setSearchTerm(term);
        setCurrentPage(1);
    }, []);

    // --- Badge de Situação ---
    const SituationBadge = useCallback(({ situation }) => {
        const config = {
            'Ativo': { className: styles.situationBadgeActive, icon: '✅' },
            'Inativo': { className: styles.situationBadgeInactive, icon: '❌' }
        };
        const style = config[situation] || config['Inativo'];
        return (
            <span className={`${styles.situationBadge} ${style.className}`}>
                {style.icon} {situation || 'Inativo'}
            </span>
        );
    }, []);

    // --- Renderização das Etapas do Preparo ---
    const renderPreparationStepsTable = useCallback(() => {
        if (preparationSteps.length === 0) {
            return (
                <div className={styles.emptyState}>
                    <div className={styles.emptyIcon}>📋</div>
                    <div className={styles.emptyTitle}>Nenhuma etapa adicionada</div>
                    <div className={styles.emptyMessage}>
                        Adicione etapas para criar o modo de preparo.
                    </div>
                </div>
            );
        }

        return (
            <div className={styles.tableContainer}>
                <table className={styles.table}>
                    <thead>
                        <tr>
                            <th style={{ width: '100px', textAlign: 'center' }}>Ordem</th>
                            <th>Descrição da Etapa</th>
                            <th style={{ width: '120px' }}>Ações</th>
                        </tr>
                    </thead>
                    <tbody>
                        {preparationSteps.map((step, index) => (
                            <tr key={step.id}>
                                <td>
                                    <div className={styles.orderControls}>
                                        <button
                                            type="button"
                                            onClick={() => handleMovePreparationStep(index, 'up')}
                                            disabled={index === 0 || formLoading}
                                            className={styles.orderButton}
                                        >
                                            ▲
                                        </button>
                                        <span className={styles.orderNumber}>
                                            {step.order || index + 1}
                                        </span>
                                        <button
                                            type="button"
                                            onClick={() => handleMovePreparationStep(index, 'down')}
                                            disabled={index === preparationSteps.length - 1 || formLoading}
                                            className={styles.orderButton}
                                        >
                                            ▼
                                        </button>
                                    </div>
                                </td>
                                <td>
                                    <input
                                        type="text"
                                        value={step.description}
                                        onChange={(e) => handleUpdatePreparationStep(step.id, e.target.value)}
                                        className={styles.input}
                                        disabled={formLoading}
                                        style={{ width: '100%' }}
                                    />
                                </td>
                                <td>
                                    <button
                                        type="button"
                                        onClick={() => handleRemovePreparationStep(step.id)}
                                        className={`${styles.actionButton} ${styles.deleteButton}`}
                                        disabled={formLoading}
                                    >
                                        Remover
                                    </button>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        );
    }, [preparationSteps, formLoading, handleMovePreparationStep, handleUpdatePreparationStep, handleRemovePreparationStep]);

    return (
        <div className={styles.pageContainer}>
            {/* Form Modal */}
            <FormModal
                isOpen={isModalOpen}
                onClose={handleCloseModal}
                onSubmit={handleSave}
                title={editingItem ? `Editar Setup: ${editingItem.name}` : 'Novo Setup de Preparo'}
                subtitle={editingItem ? `Editando: ${editingItem.name}` : undefined}
                isLoading={formLoading}
                error={formStatus.type === 'error' ? formStatus.message : ''}
                success={formStatus.type === 'success' ? formStatus.message : ''}
                submitText={editingItem ? '💾 Atualizar Setup' : '✨ Criar Setup'}
            >
                <div className={styles.inputGroup}>
                    <label htmlFor="setupName">
                        Nome do Setup <span className={styles.required}>*</span>
                    </label>
                    <input
                        id="setupName"
                        type="text"
                        value={setupName}
                        onChange={(e) => setSetupName(e.target.value)}
                        className={`${styles.input} ${nameExistsError ? styles.inputError : ''}`}
                        placeholder="Ex: Preparo Padrão de Creme, Setup Massa Base, etc."
                        disabled={formLoading}
                        autoFocus
                        required
                    />
                    {nameExistsError && (
                        <div className={styles.errorHint}>⚠️ {nameExistsError}</div>
                    )}
                </div>

                <div className={styles.inputGroup}>
                    <label htmlFor="setupDescription">Descrição</label>
                    <textarea
                        id="setupDescription"
                        value={setupDescription}
                        onChange={(e) => setSetupDescription(e.target.value)}
                        className={styles.textarea}
                        placeholder="Descreva a finalidade deste setup, produtos aplicáveis, observações importantes..."
                        rows="3"
                        disabled={formLoading}
                    />
                </div>

                <div className={styles.inputGroup}>
                    <label htmlFor="situation">Situação</label>
                    <select
                        id="situation"
                        value={situation}
                        onChange={(e) => setSituation(e.target.value)}
                        className={styles.select}
                        style={{ width: '200px' }}
                        disabled={formLoading}
                    >
                        {SITUATION_OPTIONS.map(opt => (
                            <option key={opt} value={opt}>{opt === 'Ativo' ? '✅ Ativo' : '❌ Inativo'}</option>
                        ))}
                    </select>
                </div>

                {/* Etapas do Modo de Preparo */}
                <div className={styles.preparationStepsSection}>
                    <h3>Etapas do Modo de Preparo</h3>

                    <div className={styles.inputGroup}>
                        <label htmlFor="preparationStep">Adicionar Etapa:</label>
                        <div className={styles.inlineInputGroup}>
                            <input
                                id="preparationStep"
                                type="text"
                                value={currentStep}
                                onChange={(e) => setCurrentStep(e.target.value)}
                                onKeyPress={handleStepKeyPress}
                                className={styles.input}
                                placeholder="Digite a etapa e pressione Enter ou clique em Adicionar"
                                disabled={formLoading}
                            />
                            <button
                                type="button"
                                onClick={handleAddPreparationStep}
                                className={styles.primaryButton}
                                disabled={formLoading || !currentStep.trim()}
                            >
                                + Adicionar
                            </button>
                        </div>
                    </div>

                    {renderPreparationStepsTable()}
                </div>
            </FormModal>

            {/* Delete Confirmation Modal */}
            <ConfirmModal
                isOpen={deleteConfirm.isOpen}
                onClose={() => setDeleteConfirm({ isOpen: false, id: null, name: '' })}
                onConfirm={handleConfirmDelete}
                title="⚠️ Confirmar Exclusão"
                message={`Tem certeza que deseja excluir o setup "${deleteConfirm.name}"?\n\nEsta ação não pode ser desfeita e pode afetar processos que dependem deste setup.`}
                isLoading={deleteLoading}
                danger={true}
            />

            {/* Page Header */}
            <PageHeader
                title="Setups de Preparo"
                actionText="Novo Setup"
                onAction={handleNewClick}
                disabled={listLoading || formLoading}
                showAction={can.create}
            />

            {/* Search Bar */}
            <div className={styles.searchBar}>
                <SearchBar
                    placeholder="Buscar por nome ou descrição..."
                    onSearch={handleSearch}
                    disabled={listLoading || formLoading}
                />
            </div>

            {/* Error State - Removed listError check */}
            {/* Table or Loading State */}
            <>
                {listLoading ? (
                    <TableSkeleton columns={5} rows={ITEMS_PER_PAGE} />
                ) : currentSetups.length === 0 ? (
                    <EmptyState
                        icon={searchTerm ? '🔍' : '📋'}
                        title={
                            searchTerm
                                ? 'Nenhum resultado encontrado'
                                : 'Nenhum setup cadastrado'
                        }
                        message={
                            searchTerm
                                ? '🔍 Tente ajustar sua busca.'
                                : '✨ Clique em "Novo Setup" para começar.'
                        }
                    />
                ) : (
                    <>
                        <div className={styles.tableContainer}>
                            <table className={styles.table}>
                                <thead>
                                    <tr>
                                        <th>Nome do Setup</th>
                                        <th>Descrição</th>
                                        <th style={{ textAlign: 'center', width: '100px' }}>Etapas</th>
                                        <th style={{ width: '120px' }}>Situação</th>
                                        <th style={{ width: '220px' }}>Ações</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {currentSetups.map(item => (
                                        <tr key={item.id} className={styles.tableRow}>
                                            <td className={styles.conditionName}>
                                                <strong>{item.name}</strong>
                                                {item.updatedBy && (
                                                    <div className={styles.rowMetadata}>
                                                        Última alteração: {item.updatedByName || item.updatedBy}
                                                        <br />
                                                        {item.updatedAt ? new Date(item.updatedAt).toLocaleDateString('pt-BR') : 'N/A'}
                                                    </div>
                                                )}
                                            </td>
                                            <td>
                                                <span style={{ color: 'var(--text-secondary)', fontSize: '0.9rem' }}>
                                                    {item.description || 'Sem descrição'}
                                                </span>
                                            </td>
                                            <td style={{ textAlign: 'center' }}>
                                                <span className={styles.badge}>
                                                    {item.preparationSteps?.length || 0} etapa(s)
                                                </span>
                                            </td>
                                            <td>
                                                <SituationBadge situation={item.situation} />
                                            </td>
                                            <td className={styles.actionsCell}>
                                                {can.edit && (
                                                    <button
                                                        onClick={() => handleEditClick(item)}
                                                        className={styles.actionButton}
                                                        disabled={formLoading}
                                                    >
                                                        Editar
                                                    </button>
                                                )}
                                                {can.delete && (
                                                    <button
                                                        onClick={() => handleDeleteClick(item.id, item.name)}
                                                        className={`${styles.actionButton} ${styles.deleteButton}`}
                                                        disabled={formLoading}
                                                    >
                                                        Deletar
                                                    </button>
                                                )}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>

                        <Pagination
                            currentPage={currentPage}
                            totalPages={totalPagesAfterFilter}
                            totalItems={totalFilteredItems}
                            onPageChange={handlePageChange}
                            disabled={formLoading}
                            itemsLabel="setups"
                        />
                    </>
                )}
            </>
        </div>
    );
};

export default PreparationSetupPage;